import { SignJWT, jwtVerify } from 'jose'

import { deleteCookie, getCookie, setCookie } from './cookie'
import { getTokenInfo } from '../auth'
import { signOut } from '../firebase/auth'

export type Session = {
  accessToken: string
  refreshToken: string
  firebaseToken: string
  accessTokenExpiresAt: number
  isHost: boolean
  name: string
  aid: string
  rememberMe: boolean
  blocked?: boolean
  certified?: boolean
  expiresAt?: number
}

export const MAX_AGE = 60 * 60 * 24 * 30 // 세션 만료 시간(sec) : 30일

export const isSecureCookieRequired = process.env.NODE_ENV === 'production'

export const sessionCookieName = isSecureCookieRequired
  ? '__Secure-session-token'
  : 'session-token'

// TODO: 마이그레이션 완료 후 삭제 (12/12보다 최소 30일 이후)
export const LEGACY_SESSION_COOKIE = 'session-token'

const secretKey = process.env.NEXTAUTH_SECRET
// TODO: 마이그레이션 완료 후 삭제 (1/6보다 최소 30일 이후)
const legacySecretKey = process.env.LEGACY_NEXTAUTH_SECRET // 기존 시크릿
const encodedKey = new TextEncoder().encode(secretKey)
const legacyEncodedKey = new TextEncoder().encode(legacySecretKey)

export async function encrypt(payload: Session) {
  const { rememberMe } = payload

  const jwt = new SignJWT(payload)
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()

  // rememberMe가 true일 때만 만료 시간 설정
  if (rememberMe) {
    jwt.setExpirationTime(Math.floor(Date.now() / 1000) + MAX_AGE)
  }

  return jwt.sign(encodedKey)
}

export async function decrypt(session: string | undefined = '') {
  // 1. 새 시크릿으로 먼저 시도
  try {
    const { payload } = await jwtVerify(session, encodedKey, {
      algorithms: ['HS256'],
    })

    return payload
  } catch {
    // 2. 새 시크릿 실패 시 레거시 시크릿으로 시도
    try {
      const { payload } = await jwtVerify(session, legacyEncodedKey, {
        algorithms: ['HS256'],
      })

      return payload
    } catch {
      return null
    }
  }
}

export const getSession = async () => {
  // 새 쿠키 먼저 확인
  let session = await getCookie(sessionCookieName)

  // 없으면 레거시 쿠키 확인 (마이그레이션)
  if (!session && sessionCookieName !== LEGACY_SESSION_COOKIE) {
    session = await getCookie(LEGACY_SESSION_COOKIE)
  }

  if (!session) {
    return null
  }

  const payload = await decrypt(session)

  return payload as Session
}

export async function createSession(payload: Session) {
  const { expiresAt } = payload
  const session = await encrypt(payload)

  await setCookie(sessionCookieName, session, {
    httpOnly: true,
    secure: isSecureCookieRequired,
    sameSite: 'lax',
    path: '/',
    expires: expiresAt ? new Date(expiresAt) : undefined,
  })

  // 레거시 쿠키 삭제 (마이그레이션)
  if (sessionCookieName !== LEGACY_SESSION_COOKIE) {
    await deleteCookie(LEGACY_SESSION_COOKIE)
  }
}

export async function updateSession(newSession: Partial<Session>) {
  // 새 쿠키 먼저 확인
  let session = await getCookie(sessionCookieName)

  // 없으면 레거시 쿠키 확인 (마이그레이션)
  if (!session && sessionCookieName !== LEGACY_SESSION_COOKIE) {
    session = await getCookie(LEGACY_SESSION_COOKIE)
  }

  if (!session) {
    throw new Error('session not found')
  }

  const payload = await decrypt(session)

  if (!payload) {
    throw new Error('decrypt session failed')
  }

  const encryptedSession = await encrypt({
    ...payload,
    ...newSession,
  } as Session)

  const { expiresAt } = payload as Session

  await setCookie(sessionCookieName, encryptedSession, {
    httpOnly: true,
    secure: isSecureCookieRequired,
    sameSite: 'lax',
    path: '/',
    expires: expiresAt ? new Date(expiresAt) : undefined,
  })

  // 레거시 쿠키 삭제 (마이그레이션)
  if (sessionCookieName !== LEGACY_SESSION_COOKIE) {
    await deleteCookie(LEGACY_SESSION_COOKIE)
  }
}

export async function deleteSession() {
  await deleteCookie(sessionCookieName, {
    secure: isSecureCookieRequired,
    path: '/',
  })

  // 레거시 쿠키도 삭제 (마이그레이션 기간 중 양쪽 다 삭제)
  if (sessionCookieName !== LEGACY_SESSION_COOKIE) {
    await deleteCookie(LEGACY_SESSION_COOKIE)
  }

  await signOut()
}

export async function updateSessionFromTokens({
  accessToken,
  refreshToken,
}: Pick<Session, 'accessToken' | 'refreshToken'>) {
  const { accessTokenExpiresAt, isHost, name, aid } = getTokenInfo(accessToken)

  const newSession = {
    accessToken,
    refreshToken,
    accessTokenExpiresAt,
    isHost,
    name,
    aid,
  }

  await updateSession(newSession)
}
